import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Subscription, combineLatest, forkJoin, switchMap, take, takeUntil, tap } from 'rxjs';
import { AppointmentTypeService } from 'src/app/core/services/appointment-type.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { PUBLIC_HOLIDAYS } from 'src/app/core/data/app.data';
import { UserService } from 'src/app/core/services/user.service';
import { PatientAppointmentService } from 'src/app/core/services/patient-appointment.service';
import { PatientService } from 'src/app/core/services/patient.service';
import { ScheduleService } from 'src/app/core/services/schedule.service';
import { SpeechRecognitionService } from 'src/app/core/services/speech-recognition.service';
import { FeedbackService } from 'src/app/core/services/feedback.service';
import { PatientSelectionDialogComponent } from 'src/app/shared/components/patient-selection-dialog/patient-selection-dialog.component';
import { PatientFinancialService, BillingDocument } from 'src/app/core/services/patient-financial.service';
import { GroupService } from 'src/app/core/services/group.service';
import { StatisticsService, ConnectFinancialDay, ConnectFinancialPatientDay } from '../../services/statistics.service';
import { isConnectEnabledForGroup } from 'src/app/shared/util/connect.util';
import { formatRand } from 'src/app/shared/util/currency.util';

interface NativeDayFinancialSummary {
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;
}

interface SelectedDayFinancialSummary extends NativeDayFinancialSummary {
  cashAmount: number;
  electronicAmount: number;
  creditedAmount: number;
}

interface ConnectDayDisplay {
  day: ConnectFinancialDay;
  claimed: string;
  approved: string;
  rejected: string;
  patientLiable: string;
  paid: string;
  outstanding: string;
  clawback: string;
}

interface ConnectPatientDayDisplay {
  day: ConnectFinancialPatientDay;
  /** All-zero cents with codeCount > 0: claim submitted, awaiting adjudication. */
  awaitingResponse: boolean;
  claimed: string;
  paid: string;
  outstanding: string;
}

@Component({
  selector: 'app-financial-calendar',
  templateUrl: './financial-calendar.component.html',
  styleUrls: ['./financial-calendar.component.scss'],
  /*  changeDetection: ChangeDetectionStrategy.OnPush, */
  animations: [
    trigger('rowAnimation', [
      // Enter transition
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'scaleY(0)',
          transformOrigin: 'top center'
        }),
        animate('0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)', style({
          opacity: 1,
          transform: 'scaleY(1)'
        })),
      ]),
      // Leave transition
      transition(':leave', [
        animate('0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)', style({
          opacity: 0,
          transform: 'scaleY(0)',
          transformOrigin: 'top center'
        }))
      ])
    ])
  ],
})
export class FinancialCalendarComponent extends BaseComponent implements OnInit, OnDestroy {

  appointments: any[] | null = null;
  appointmentsForSelectedDay: any[] | null = null;

  // Financial data properties
  billingDocuments: BillingDocument[] = [];
  appointmentBillingData: any[] = []; // Store the complex structure from backend

  currentMonth = new Date();
  dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  days: any[] = [];
  months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  selectedMonth: number = new Date().getMonth();
  selectedYear: number = new Date().getFullYear();
  selectedDay: Date | null = null;
  startOfWeekIndex: number | null = null;

  availableDays: string[] = [];
  appointmentTypes: string[] = [];
  startTime: string = '00:00';
  endTime: string = '23:59';
  timeSlotDuration: string = '30'


  isDoctor?: boolean;
  group?: any;
  financialDocumentsExpanded: boolean = true;
  appointmentsExpanded: boolean = true;
  financialDocsExpanded: { [appointmentId: string]: boolean } = {};
  public unlinkedDocsExpanded: boolean = true;

  private loadByMonthSubscription?: Subscription
  private patientsSubscription?: Subscription;


  /* TODO: dynamically load this from somewhere online to ensure frequent updates */
  private publicHolidays = PUBLIC_HOLIDAYS;

  private allBillingDocuments: BillingDocument[] = [];
  private unlinkedBillingDocuments: BillingDocument[] = [];

  private unlinkedFinancialDocumentsGroupedCache: any[] = [];
  private unlinkedFinancialDocumentsGroupedCacheKey: string = '';

  // Connect (medical-aid claims) state — gated on environment + group connect settings
  connectEnabled: boolean = false;
  connectSummaryForSelectedDay: ConnectDayDisplay | null = null;
  connectNoAppointmentExpanded: boolean = true;
  private connectDaysByDate = new Map<string, ConnectDayDisplay>();
  // Per (patient, day) connect rows. Matching to appointments is by (patientId, dateKey)
  // only — PatientProcedureCodes carries no appointmentId (route-2 inference), so a
  // patient with two appointments the same day shows the same connect row on both.
  private connectPatientDaysByDate = new Map<string, ConnectFinancialPatientDay[]>();
  private connectPatientRowsForSelectedDay = new Map<string, ConnectPatientDayDisplay>();
  private connectNoAppointmentRowsCache: ConnectPatientDayDisplay[] = [];
  private connectNoAppointmentRowsCacheKey: string = '';
  private connectFinancialsSubscription?: Subscription;

  // Stable empty defaults so template-bound getters return the same reference every cycle
  private static readonly EMPTY_DOCUMENTS: BillingDocument[] = [];
  private static readonly EMPTY_APPOINTMENTS: any[] = [];
  private static readonly EMPTY_CONNECT_PATIENT_ROWS: ConnectPatientDayDisplay[] = [];
  private static readonly EMPTY_DAY_SUMMARY: NativeDayFinancialSummary = { totalInvoiced: 0, totalPaid: 0, totalOutstanding: 0 };
  private static readonly EMPTY_SELECTED_DAY_SUMMARY: SelectedDayFinancialSummary = {
    totalInvoiced: 0, totalPaid: 0, totalOutstanding: 0, cashAmount: 0, electronicAmount: 0, creditedAmount: 0
  };

  // Precomputed lookup maps (rebuilt on data load) so template-bound getters are O(1)
  // instead of re-scanning the document/appointment arrays on every change detection cycle
  private billingDocsByDate = new Map<string, BillingDocument[]>();
  private billingDocsByAppointmentId = new Map<string, BillingDocument[]>();
  private sortedBillingDocsByAppointmentId = new Map<string, BillingDocument[]>();
  private docsByCoreBillingNumber = new Map<string, BillingDocument[]>();
  private financialStatusByDate = new Map<string, string>();
  private financialSummaryByDate = new Map<string, NativeDayFinancialSummary>();
  private financialSummaryWithUnlinkedByDate = new Map<string, NativeDayFinancialSummary>();
  private estimateTotalByDate = new Map<string, number>();
  private appointmentsByDate = new Map<string, any[]>();
  private totalAppointmentsByDate = new Map<string, number>();
  private invoicedAppointmentsByDate = new Map<string, number>();
  private billingDocumentsForSelectedDayCache: BillingDocument[] = FinancialCalendarComponent.EMPTY_DOCUMENTS;
  private financialSummaryForSelectedDayCache: SelectedDayFinancialSummary = FinancialCalendarComponent.EMPTY_SELECTED_DAY_SUMMARY;


  constructor(
    private patientFinancialService: PatientFinancialService,
    private statisticsService: StatisticsService,
    private groupService: GroupService,
    private userService: UserService,
    private patientAppointmentService: PatientAppointmentService,
    private patientService: PatientService,
    private scheduleService: ScheduleService,
    private appointmentTypeService: AppointmentTypeService,
    private speechService: SpeechRecognitionService,
    private router: Router,
    private cd: ChangeDetectorRef,
    private snack: MatSnackBar,
    dialog: MatDialog,
    fs: FeedbackService,
  ) {
    super(fs, dialog);
  }

  ngOnInit(): void {
    combineLatest([
      this.scheduleService.schedule$,
      this.appointmentTypeService.appointmentTypes$,
      this.patientAppointmentService.monthAppointments$
    ]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(([settings, appointmentTypes, appointments]) => {
      if (settings) {
        this.availableDays = settings.availableDays;
        this.startTime = settings.startTime;
        this.endTime = settings.endTime;
        this.timeSlotDuration = settings.timeSlotDuration;
      }
      if (appointmentTypes) {
        this.appointmentTypes = appointmentTypes;
      }
      if (appointments) {
        this.appointments = this.sortAppointments(appointments || []);
        this.rebuildAppointmentCountCaches();
        this.updateAppointmentsForSelectedDay();
      }

      this.generateCalendar();
    });


    this.speechService.getDetectedCommand().subscribe(command => {
      if (command.command === 'SHOW_APPOINTMENTS' && command.payload instanceof Date) {
        const targetDate = command.payload;

        // Check if the target date is within the current visible month and year
        if (targetDate.getMonth() !== this.currentMonth.getMonth() || targetDate.getFullYear() !== this.currentMonth.getFullYear()) {
          this.selectedMonth = targetDate.getMonth();
          this.selectedYear = targetDate.getFullYear();

          this.onMonthYearChange(); // Update the calendar view to the selected month and year
        }

        // Find the target day within the updated calendar view
        const selectedDay = this.days.find(day => day.date &&
          day.date.getDate() === targetDate.getDate() &&
          day.date.getMonth() === targetDate.getMonth() &&
          day.date.getFullYear() === targetDate.getFullYear()
        );

        // Open the selected day
        if (selectedDay) {
          this.selectDay(selectedDay);
        }
      }
    });

    this.userService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.isDoctor = result?.userType === 'doctor'
      });

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(group => {
        this.group = group;
        // Load financial data for the current month
        this.loadFinancialDataForCurrentMonth();

      })
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

    if (this.loadByMonthSubscription) {
      this.loadByMonthSubscription.unsubscribe();
    }

    if (this.patientsSubscription) {
      this.patientsSubscription.unsubscribe();
    }

    if (this.connectFinancialsSubscription) {
      this.connectFinancialsSubscription.unsubscribe();
    }
  }

  shouldShowArrivalOptions(appointment: any): boolean {
    if (this.isDoctor) return false;
    return moment(appointment.date).isSame(moment(), 'day');
  }

  buildDiagnosisString(patient: any): string {
    const { diagnosis2, diagnosis3, diagnosis4, diagnosis5 } = patient;
    const diagnoses = [diagnosis2, diagnosis3, diagnosis4, diagnosis5].filter(Boolean); // Filter out falsy values
    const diagnosisString = diagnoses.join(', ');
    return diagnosisString.length > 70 ? `${diagnosisString.substring(0, 70)}...` : diagnosisString;
  }

  private sortAppointments(appointments: any[] | null): any[] | null {
    if (!appointments?.length) {
      return appointments;
    }

    return _.chain(appointments)
      .map(appointment => {
        // Handle appointments with a date property
        let typeDetails;
        typeDetails = this.appointmentTypes.find((type: any) => type.id === appointment.appointmentTypeId) ?? undefined;
        if (!typeDetails) {
          typeDetails = this.appointmentTypes.find((type: any) => type.type === appointment.appointmentType) ?? undefined;
        }

        return {
          ...appointment,
          typeDetails: typeDetails ?? { type: appointment.appointmentType, backgroundColor: '#fff', textColor: '#000', borderColor: '#fff' } // Default if not found
        };
      })
      .sortBy(appointment => appointment.timeSlot)
      .value()
      .reverse();
  }

  onMonthYearChange(): void {
    this.currentMonth = new Date(this.selectedYear, this.selectedMonth);
    this.generateCalendar();
    this.loadAppointmentsForMonth();
    this.loadFinancialDataForCurrentMonth();
  }


  trackByDate(index: number, day: any): any {
    return day.date;
  }

  resetOpenRows() {
    if (!this.selectedDay) return
    this.selectedDay = null;
    this.appointmentsForSelectedDay = null;
    this.startOfWeekIndex = null;
    this.updateSelectedDayFinancialCaches();
  }

  generateCalendar(): void {
    const firstDay = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 1);
    const lastDay = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 0);
    const daysInMonth = lastDay.getDate();
    const firstDayOfWeek = firstDay.getDay();

    const newDays = [];
    for (let i = 0; i < firstDayOfWeek; i++) {
      newDays.push({ date: null }); // Fill empty days until the first day of the month
    }
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), i);
      newDays.push({
        date: date,
        appointmentCounts: this.getAppointmentCountsForDate(date),
        financialStatus: this.getFinancialStatusForDate(date)
      });
    }
    this.days = newDays;
  }


  private getAppointmentCountsForDate(date: Date): any[] {
    if (!this.appointments || !this.appointmentTypes) return [];

    const appointmentsForDate = this.appointmentsByDate.get(this.getDateKey(date)) || [];
    const groupedByType: { [key: string]: { count: number, details: any } } = {};

    appointmentsForDate.forEach(appointment => {
      const type = appointment.appointmentType || 'No Type';
      if (!groupedByType[type]) {
        groupedByType[type] = { count: 0, details: appointment.typeDetails || { type: appointment.appointmentType, backgroundColor: '#fff', textColor: '#000', borderColor: '#fff' } };
      }
      groupedByType[type].count++;
    });

    return Object.keys(groupedByType).map(type => ({
      type: type,
      count: groupedByType[type].count,
      backgroundColor: groupedByType[type].details.backgroundColor || '#202123',
      textColor: groupedByType[type].details.textColor || '#202123',
      borderColor: groupedByType[type].details.borderColor || groupedByType[type].details.backgroundColor
    }));
  }





  hasHoliday(date: Date): boolean {
    if (!date) return false;
    const dateMoment = moment(date).startOf('day');
    return this.publicHolidays?.some(holiday =>
      dateMoment.isSame(moment(holiday.date).startOf('day'), 'day')
    ) || false;
  }

  getHoliday(date: Date): any {
    const holiday = this.publicHolidays.find(holiday =>
      moment(date).isSame(moment(holiday.date), 'day')
    );
    return holiday || null;
  }

  getPublicHolidayDetails = _.memoize((date: Date) => {
    if (!this.hasHoliday(date)) {
      return null;
    }
    return this.getHoliday(date);
  }, date => moment(date).format('YYYY-MM-DD'));


  isToday(date: Date): boolean {
    const today = new Date();
    return date?.getDate() === today.getDate() &&
      date?.getMonth() === today.getMonth() &&
      date?.getFullYear() === today.getFullYear();
  }

  isAvailable(date: Date): boolean {
    if (!date) return false;
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });

    return this.availableDays.includes(dayName); // Date is available
  }

  isFutureDate(date: Date) {

    const dateMoment = moment(date);
    // Check if date is in the past
    if (!dateMoment.isAfter(moment().subtract(1, 'days'))) {
      return false;
    }

    return true;
  }

  navigate(monthIncrement: number): void {
    let newMonth = new Date(this.currentMonth);
    // Set date to 1 before changing months
    newMonth.setDate(1);
    newMonth.setMonth(newMonth.getMonth() + monthIncrement);
    this.currentMonth = new Date(newMonth);
    this.generateCalendar();
    this.loadAppointmentsForMonth();
    this.loadFinancialDataForCurrentMonth();
  }


  loadAppointmentsForMonth() {
    if (this.loadByMonthSubscription) {
      this.loadByMonthSubscription.unsubscribe();
    }

    this.loadByMonthSubscription = this.patientAppointmentService.loadAppointmentsByMonth(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1).subscribe();
  }


  selectDay(selectedDay: any): void {
    if (!this.isAvailable(selectedDay.date)) return;
    if (this.selectedDay && selectedDay.date && this.selectedDay.getTime() === selectedDay.date.getTime()) {
      this.selectedDay = null;
      this.appointmentsForSelectedDay = null;
      this.startOfWeekIndex = null;
    } else {
      this.selectedDay = selectedDay.date;
      this.startOfWeekIndex = this.days.findIndex(day => day.date && day.date.getTime() === selectedDay.date.getTime());
      this.updateAppointmentsForSelectedDay(); // Call the method to update appointmentsForSelectedDay
    }
    this.updateSelectedDayFinancialCaches();
  }

  private updateAppointmentsForSelectedDay(): void {

    //TODO: map to appointments to determine if note exists in timeline for matching  ai request for day
    if (this.selectedDay) {
      const filteredAppointments = this.appointments?.filter(appointment => {
        const appointmentDate = moment(appointment.date);
        return appointmentDate.isSame(this.selectedDay, 'day');
      }) || [];

      // Calculate appointment start and end times and store them in the appointment objects
      const duration = parseInt(this.timeSlotDuration, 10);
      filteredAppointments.forEach(appointment => {
        const appointmentDate = moment(appointment.date);
        const [startHour, startMinute] = appointment.timeSlot.split(':').map(Number);

        const appointmentStart = appointmentDate.clone().set({
          hour: startHour,
          minute: startMinute,
          second: 0,
          millisecond: 0
        });

        let appointmentEnd: moment.Moment;

        if (appointment.endSlot) {
          const [endHour, endMinute] = appointment.endSlot.split(':').map(Number);
          appointmentEnd = appointmentDate.clone().set({
            hour: endHour,
            minute: endMinute,
            second: 0,
            millisecond: 0
          });
        } else {
          appointmentEnd = appointmentStart.clone().add(duration, 'minutes');
        }

        appointment.appointmentStart = appointmentStart;
        appointment.appointmentEnd = appointmentEnd;
      });

      const sortedAppointments = _.sortBy(filteredAppointments, appointment => {
        return appointment.appointmentStart.valueOf();
      });


      this.appointmentsForSelectedDay = sortedAppointments;
      this.cd.detectChanges();
    } else {
      this.appointmentsForSelectedDay = null;
    }
  }


  private generateTimeSlots(): Array<{ start: moment.Moment, end: moment.Moment }> {
    const slots = [];
    let start = moment(this.selectedDay).set({
      hour: parseInt(this.startTime.split(':')[0], 10),
      minute: parseInt(this.startTime.split(':')[1], 10),
      second: 0,
      millisecond: 0
    });

    const end = moment(this.selectedDay).set({
      hour: parseInt(this.endTime.split(':')[0], 10),
      minute: parseInt(this.endTime.split(':')[1], 10),
      second: 0,
      millisecond: 0
    });

    const duration = parseInt(this.timeSlotDuration, 10);

    while (start.clone().add(duration, 'minutes') <= end) {
      const slotStart = start.clone();
      const slotEnd = start.clone().add(duration, 'minutes');
      slots.push({ start: slotStart, end: slotEnd });
      start = slotEnd;
    }

    return slots;
  }



  getAppointmentRowStart(): number | null {
    if (this.startOfWeekIndex !== null) {
      // Calculate which row the selected day's week ends on, then add 1 for the next row
      // Divide by 7 (days in a week) to find the week number, then add 1 for the header, 
      // and another +1 to move to the row below the current week.
      return Math.ceil((this.startOfWeekIndex + 1) / 7) + 1 + 1; // Adding +1 twice: once for header, once to move below
    }
    return null;
  }

  getAppointmentColumnSpan(): string {
    // Span across all 7 columns of the week
    return '1 / span 7';
  }



  


  

  

  


  navigateToPatientFile(patientId: string) {
    this.patientService.getPatientById(patientId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        // Navigate to the patient file in the split screen
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['file', patientId],
          }
        }]);
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error loading the patient file. Please try again.');
      });
  }


  async loadVirtualLink(appointment: any, event: MouseEvent) {
    if (this.patientService.isRecordingLocked()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (this.patientService.isRecordingLocked()) {
      return;
    }

    let link = appointment.virtualLink.trim();
    // Ensure the link has a protocol
    if (!link.match(/^https?:\/\//)) {
      link = 'http://' + link;
    }

    let startRecording = false;
    if (appointment.Patient) {
      this.patientService.triggerAiRecording.next(false);
      startRecording = await this.feedbackService.showConfirmation('Start Recording?', 'You are about to enter a virtual consultation link. Would you like to start an AI-generated recording for the consultation? Remember to return later to stop the recording once you\'re done.', [], 'Go To Meeting Only', 'Go To Meeting + Record');
    }

    if (startRecording) {
      const patientId = appointment.Patient.id;
      // Clear the current patient
      /* this.patientService.triggerAiRecording.next(false); */
      this.patientService.setCurrentPatient(null);
      // Clear the current patient view
      this.router.navigate(['/patients', { outlets: { patient: null } }]);
      setTimeout(() => {
        // Load the patient file linked to this appointment
        const sub = this.patientService.getPatientById(patientId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              // Navigate to the patient's file view
              this.router.navigate(['/patients', {
                outlets: { patient: ['file', patientId] }
              }]);
              // Trigger AI recording auto popup
              this.patientService.triggerAiRecording.next(true);
              // Open the link after a brief delay
              setTimeout(() => window.open(link, '_blank'), 2500);
              sub.unsubscribe();
            },
            error: error => {
              console.error(error);
              // Stop AI recording on error
              this.patientService.triggerAiRecording.next(false);
              this.showToastMessage('There was an error loading the patient file. Please try again.');
              sub.unsubscribe();
            }
          });
      }, 500);


    } else {
      window.open(link, '_blank');
    }
  }

  // Financial data methods
  private loadFinancialDataForCurrentMonth(): void {
    if (!this.group || !this.group.id) {
      console.warn('No group available, skipping financial data load');
      return;
    }

    const startDate = moment(this.currentMonth).startOf('month').format('YYYY-MM-DD');
    const endDate = moment(this.currentMonth).endOf('month').format('YYYY-MM-DD');

    this.loadConnectFinancialsForCurrentMonth(startDate, endDate);

    this.patientFinancialService.getBillingDocumentsByDateRange(this.group.id, startDate, endDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          // New backend structure: { appointmentBillingData, unlinkedBillingDocuments }
          this.appointmentBillingData = data.appointmentBillingData || [];
          // Flatten all billing documents from appointmentBillingData
          this.billingDocuments = this.appointmentBillingData.reduce((allDocs: BillingDocument[], item: any) => {
            if (item.billingDocuments && Array.isArray(item.billingDocuments)) {
              return allDocs.concat(item.billingDocuments);
            }
            return allDocs;
          }, []);
          // Use unlinkedBillingDocuments directly from backend
          this.unlinkedBillingDocuments = data.unlinkedBillingDocuments || [];
          this.rebuildFinancialCaches();
          this.generateCalendar();
        },
        error: (error) => {
          console.error('Error loading billing documents:', error);
        }
      });
  }

  /**
   * Loads the connect (medical-aid claims) roll-up for the visible month when the
   * connect gating passes; otherwise clears all connect state so nothing renders.
   */
  private loadConnectFinancialsForCurrentMonth(startDate: string, endDate: string): void {
    this.connectEnabled = isConnectEnabledForGroup(this.group);

    if (this.connectFinancialsSubscription) {
      this.connectFinancialsSubscription.unsubscribe();
    }

    if (!this.connectEnabled) {
      this.connectDaysByDate = new Map<string, ConnectDayDisplay>();
      this.connectPatientDaysByDate = new Map<string, ConnectFinancialPatientDay[]>();
      this.updateSelectedDayFinancialCaches();
      return;
    }

    this.connectFinancialsSubscription = this.statisticsService.getConnectFinancials(this.group.id, startDate, endDate, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          const connectDaysByDate = new Map<string, ConnectDayDisplay>();
          const connectPatientDaysByDate = new Map<string, ConnectFinancialPatientDay[]>();
          if (data?.enabled) {
            (data.days || []).forEach(day => {
              // Freshly acknowledged claims have codes but all-zero financials;
              // skip those days entirely so no "R0.00" badge/block renders.
              const hasFinancials = (day.billedCents || 0) !== 0
                || (day.paidCents || 0) !== 0
                || (day.rejectedCents || 0) !== 0
                || (day.patientLiableCents || 0) !== 0
                || (day.clawbackCents || 0) !== 0;
              if (hasFinancials) {
                connectDaysByDate.set(day.date, this.buildConnectDayDisplay(day));
              }
            });
            // Per-patient rows are NOT all-zero-suppressed: codeCount > 0 with zero
            // cents renders as the per-appointment "Awaiting response" state.
            (data.patientDays || []).forEach(patientDay => {
              const existing = connectPatientDaysByDate.get(patientDay.date);
              if (existing) {
                existing.push(patientDay);
              } else {
                connectPatientDaysByDate.set(patientDay.date, [patientDay]);
              }
            });
          }
          this.connectDaysByDate = connectDaysByDate;
          this.connectPatientDaysByDate = connectPatientDaysByDate;
          this.updateSelectedDayFinancialCaches();
        },
        error: (error) => {
          console.error('Error loading connect financials:', error);
          this.connectDaysByDate = new Map<string, ConnectDayDisplay>();
          this.connectPatientDaysByDate = new Map<string, ConnectFinancialPatientDay[]>();
          this.updateSelectedDayFinancialCaches();
        }
      });
  }

  private buildConnectDayDisplay(day: ConnectFinancialDay): ConnectDayDisplay {
    return {
      day,
      claimed: formatRand(day.billedCents),
      approved: formatRand(day.approvedCents),
      rejected: formatRand(day.rejectedCents),
      patientLiable: formatRand(day.patientLiableCents),
      paid: formatRand(day.paidCents),
      outstanding: formatRand(day.outstandingCents),
      clawback: formatRand(day.clawbackCents)
    };
  }

  private buildConnectPatientDayDisplay(day: ConnectFinancialPatientDay): ConnectPatientDayDisplay {
    const hasFinancials = (day.billedCents || 0) !== 0
      || (day.paidCents || 0) !== 0
      || (day.rejectedCents || 0) !== 0
      || (day.patientLiableCents || 0) !== 0
      || (day.clawbackCents || 0) !== 0;
    return {
      day,
      awaitingResponse: !hasFinancials && (day.codeCount || 0) > 0,
      claimed: formatRand(day.billedCents),
      paid: formatRand(day.paidCents),
      outstanding: formatRand(day.outstandingCents)
    };
  }

  getConnectDayForDate(date: Date): ConnectDayDisplay | null {
    if (!date) return null;
    return this.connectDaysByDate.get(this.getDateKey(date)) || null;
  }

  /**
   * Connect roll-up for this appointment's patient on the selected day.
   * Matched by (patientId, dateKey) only — known route-2 limitation: a patient
   * with two appointments the same day shows the same connect row on both.
   */
  getConnectSummaryForAppointment(appointment: any): ConnectPatientDayDisplay | null {
    if (!appointment?.Patient) return null;
    return this.connectPatientRowsForSelectedDay.get(appointment.Patient.id) || null;
  }

  /**
   * Connect rows for the selected day whose patient has NO appointment that day
   * (echoes the unlinked-financial-documents grouping). Memoized like
   * getUnlinkedFinancialDocumentsGroupedByPatient since it depends on both the
   * connect rows and the appointments, which load independently.
   */
  getConnectClaimsWithoutAppointment(): ConnectPatientDayDisplay[] {
    if (!this.selectedDay) return FinancialCalendarComponent.EMPTY_CONNECT_PATIENT_ROWS;
    const dateKey = this.getDateKey(this.selectedDay);
    const patientDays = this.connectPatientDaysByDate.get(dateKey) || [];
    if (patientDays.length === 0) return FinancialCalendarComponent.EMPTY_CONNECT_PATIENT_ROWS;

    const appointments = this.appointmentsForSelectedDay || [];
    const cacheKey = `${dateKey}_${patientDays.length}_${appointments.length}`;
    if (this.connectNoAppointmentRowsCacheKey === cacheKey) {
      return this.connectNoAppointmentRowsCache;
    }

    const patientIdsWithAppointments = new Set(
      appointments.filter(appointment => appointment.Patient).map(appointment => appointment.Patient.id)
    );
    this.connectNoAppointmentRowsCache = patientDays
      .filter(patientDay => !patientIdsWithAppointments.has(patientDay.patientId))
      .map(patientDay => this.buildConnectPatientDayDisplay(patientDay))
      .sort((a, b) => a.day.patientName.localeCompare(b.day.patientName));
    this.connectNoAppointmentRowsCacheKey = cacheKey;
    return this.connectNoAppointmentRowsCache;
  }

  /** Local-date key (YYYY-MM-DD) matching moment's local-day comparisons. */
  private getDateKey(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  /**
   * Rebuilds all per-day / per-appointment lookup maps from the loaded billing data.
   * Output semantics of the public getters are unchanged; only the scan-per-call
   * computation moved here so it runs once per data load.
   */
  private rebuildFinancialCaches(): void {
    const groupByIssueDate = (docs: BillingDocument[]): Map<string, BillingDocument[]> => {
      const byDate = new Map<string, BillingDocument[]>();
      (docs || []).forEach(doc => {
        const key = moment(doc.issueDate).format('YYYY-MM-DD');
        const existing = byDate.get(key);
        if (existing) {
          existing.push(doc);
        } else {
          byDate.set(key, [doc]);
        }
      });
      return byDate;
    };

    const linkedByDate = groupByIssueDate(this.billingDocuments);
    const unlinkedByDate = groupByIssueDate(this.unlinkedBillingDocuments);

    const byAppointmentId = new Map<string, BillingDocument[]>();
    const sortedByAppointmentId = new Map<string, BillingDocument[]>();
    (this.appointmentBillingData || []).forEach(item => {
      if (item.appointment && item.appointment.id) {
        const docs: BillingDocument[] = item.billingDocuments || [];
        byAppointmentId.set(item.appointment.id, docs);
        sortedByAppointmentId.set(item.appointment.id,
          docs.slice().sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
      }
    });

    const byCoreBillingNumber = new Map<string, BillingDocument[]>();
    [...(this.billingDocuments || []), ...(this.unlinkedBillingDocuments || [])].forEach(doc => {
      if (!doc.billingNumber) return;
      const coreNumber = doc.billingNumber.replace(/^(INV-|RCPT-|EST-)/, '');
      const existing = byCoreBillingNumber.get(coreNumber);
      if (existing) {
        existing.push(doc);
      } else {
        byCoreBillingNumber.set(coreNumber, [doc]);
      }
    });

    const statusByDate = new Map<string, string>();
    const summaryByDate = new Map<string, NativeDayFinancialSummary>();
    const summaryWithUnlinkedByDate = new Map<string, NativeDayFinancialSummary>();
    const estimateByDate = new Map<string, number>();
    const allDateKeys = new Set([...linkedByDate.keys(), ...unlinkedByDate.keys()]);
    allDateKeys.forEach(key => {
      const linkedDocs = linkedByDate.get(key) || [];
      const allDocs = linkedDocs.concat(unlinkedByDate.get(key) || []);
      statusByDate.set(key, this.computeFinancialStatusForDocuments(linkedDocs));
      summaryByDate.set(key, this.computeDaySummaryForDocuments(linkedDocs));
      summaryWithUnlinkedByDate.set(key, this.computeDaySummaryForDocuments(allDocs));
      estimateByDate.set(key, allDocs
        .filter(doc => doc.documentType === 'estimate')
        .reduce((sum, doc) => sum + (doc.totalDue || 0), 0));
    });

    this.billingDocsByDate = linkedByDate;
    this.billingDocsByAppointmentId = byAppointmentId;
    this.sortedBillingDocsByAppointmentId = sortedByAppointmentId;
    this.docsByCoreBillingNumber = byCoreBillingNumber;
    this.financialStatusByDate = statusByDate;
    this.financialSummaryByDate = summaryByDate;
    this.financialSummaryWithUnlinkedByDate = summaryWithUnlinkedByDate;
    this.estimateTotalByDate = estimateByDate;

    // Invalidate caches derived from the (replaced) document arrays
    this.unlinkedFinancialDocumentsGroupedCacheKey = '';
    this.rebuildAppointmentCountCaches();
    this.updateSelectedDayFinancialCaches();
  }

  /**
   * Rebuilds the per-day appointment maps (also depends on the billing maps for
   * the invoiced counts, so it is re-run after each financial data load too).
   */
  private rebuildAppointmentCountCaches(): void {
    const byDate = new Map<string, any[]>();
    (this.appointments || []).forEach(appointment => {
      const key = moment(appointment.date).format('YYYY-MM-DD');
      const existing = byDate.get(key);
      if (existing) {
        existing.push(appointment);
      } else {
        byDate.set(key, [appointment]);
      }
    });

    const totalByDate = new Map<string, number>();
    const invoicedByDate = new Map<string, number>();
    byDate.forEach((appointmentsForDate, key) => {
      // Count only actual appointments (not events/reasons)
      totalByDate.set(key, appointmentsForDate.filter(appointment => !appointment.reason).length);

      let invoicedCount = 0;
      appointmentsForDate.forEach(appointment => {
        // Skip events/reasons
        if (appointment.reason) return;
        // Skip cancelled or no-show appointments
        if (appointment.isCancelled === true || appointment.didNotAttend === true) return;
        // Check if this appointment has any invoice documents (not just estimates)
        const appointmentBillingDocs = this.billingDocsByAppointmentId.get(appointment.id) || [];
        if (appointmentBillingDocs.some(doc => doc.documentType === 'invoice')) {
          invoicedCount++;
        }
      });
      invoicedByDate.set(key, invoicedCount);
    });

    this.appointmentsByDate = byDate;
    this.totalAppointmentsByDate = totalByDate;
    this.invoicedAppointmentsByDate = invoicedByDate;
  }

  /** Recomputes the selected-day caches (native documents/summary + connect block). */
  private updateSelectedDayFinancialCaches(): void {
    // Connect maps or the selected day changed — force the no-appointment group to recompute
    this.connectNoAppointmentRowsCacheKey = '';

    if (!this.selectedDay) {
      this.billingDocumentsForSelectedDayCache = FinancialCalendarComponent.EMPTY_DOCUMENTS;
      this.financialSummaryForSelectedDayCache = FinancialCalendarComponent.EMPTY_SELECTED_DAY_SUMMARY;
      this.connectSummaryForSelectedDay = null;
      this.connectPatientRowsForSelectedDay = new Map<string, ConnectPatientDayDisplay>();
      return;
    }

    const key = this.getDateKey(this.selectedDay);
    // Sort by createdAt in descending order (newest first)
    const documents = (this.billingDocsByDate.get(key) || []).slice().sort((a, b) => {
      return moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf();
    });
    this.billingDocumentsForSelectedDayCache = documents;
    this.financialSummaryForSelectedDayCache = this.computeSelectedDaySummary(documents);
    this.connectSummaryForSelectedDay = this.connectDaysByDate.get(key) || null;

    const connectPatientRows = new Map<string, ConnectPatientDayDisplay>();
    (this.connectPatientDaysByDate.get(key) || []).forEach(patientDay => {
      connectPatientRows.set(patientDay.patientId, this.buildConnectPatientDayDisplay(patientDay));
    });
    this.connectPatientRowsForSelectedDay = connectPatientRows;
  }

  private computeFinancialStatusForDocuments(documents: BillingDocument[]): string {
    const documentsForDate = documents.filter(doc => doc.documentType === 'invoice');

    if (documentsForDate.length === 0) return 'none';

    // Check if any invoice is unpaid
    const hasUnpaid = documentsForDate.some(doc => doc.status === 'pending' || doc.status === 'overdue');
    if (hasUnpaid) return 'unpaid';

    // Check if any invoice is partially paid
    const hasPartiallyPaid = documentsForDate.some(doc =>
      doc.status === 'partially_paid' ||
      (doc.amountPaid > 0 && doc.amountPaid < doc.totalDue)
    );
    if (hasPartiallyPaid) return 'partially_paid';

    // Check if all invoices are paid
    const allPaid = documentsForDate.every(doc =>
      doc.status === 'paid' ||
      (doc.amountPaid >= doc.totalDue)
    );
    if (allPaid) return 'paid';

    return 'none';
  }

  private computeDaySummaryForDocuments(documents: BillingDocument[]): NativeDayFinancialSummary {
    const invoices = documents.filter(doc => doc.documentType === 'invoice');
    const totalInvoiced = invoices.reduce((sum, doc) => sum + (doc.totalDue || 0), 0);
    const totalPaid = invoices.reduce((sum, doc) => sum + (doc.amountPaid || 0), 0);
    const totalOutstanding = totalInvoiced - totalPaid;
    return { totalInvoiced, totalPaid, totalOutstanding };
  }

  getFinancialStatusForDate(date: Date): string {
    if (!date) return 'none';
    return this.financialStatusByDate.get(this.getDateKey(date)) || 'none';
  }

  getFinancialStatusColor(status: string): string {
    switch (status) {
      case 'none':
        return 'transparent';
      case 'unpaid':
        return '#f44336'; // Red
      case 'partially_paid':
        return '#ff9800'; // Orange
      case 'paid':
        return '#4caf50'; // Green
      default:
        return 'transparent';
    }
  }

  getFinancialStatusTooltip(status: string): string {
    switch (status) {
      case 'none':
        return 'No invoice for this day';
      case 'unpaid':
        return 'Has invoice that has not been paid';
      case 'partially_paid':
        return 'Has invoice that has been partially paid';
      case 'paid':
        return 'Has invoice that has been paid';
      default:
        return 'Unknown status';
    }
  }

  getBillingDocumentsForSelectedDay(): BillingDocument[] {
    return this.billingDocumentsForSelectedDayCache;
  }

  /**
   * Get billing documents for a specific appointment
   */
  getBillingDocumentsForAppointment(appointmentId: string): BillingDocument[] {
    return this.billingDocsByAppointmentId.get(appointmentId) || FinancialCalendarComponent.EMPTY_DOCUMENTS;
  }

  /**
   * Helper function to categorize payment methods into our three categories
   */
  private categorizePaymentMethod(paymentMethod: string): 'cash' | 'electronic' | 'credited' {
    const method = paymentMethod.toLowerCase();

    // Cash payments
    if (method === 'cash') {
      return 'cash';
    }

    // Electronic payments
    if (method === 'card' ||
      method === 'yoco: card machine' ||
      method === 'yoco: online' ||
      method === 'eft: patient' ||
      method === 'eft: medical scheme') {
      return 'electronic';
    }

    // Credit payments
    if (method.startsWith('credit:')) {
      return 'credited';
    }

    // Default fallback - treat as electronic
    return 'electronic';
  }

  getFinancialSummaryForSelectedDay(): SelectedDayFinancialSummary {
    return this.financialSummaryForSelectedDayCache;
  }

  private computeSelectedDaySummary(documents: BillingDocument[]): SelectedDayFinancialSummary {
    const invoices = documents.filter(doc => doc.documentType === 'invoice');
    const receipts = documents.filter(doc => doc.documentType === 'receipt');

    const totalInvoiced = invoices.reduce((sum, doc) => sum + doc.totalDue, 0);
    const totalPaid = invoices.reduce((sum, doc) => sum + doc.amountPaid, 0);
    const totalOutstanding = totalInvoiced - totalPaid;

    // Calculate payment method breakdown from payment allocations and receipts
    let cashAmount = 0;
    let electronicAmount = 0;
    let creditedAmount = 0;

    // Process payment allocations from invoices
    invoices.forEach(invoice => {
      const invoiceAny = invoice as any;
      if (invoiceAny.PatientPaymentAllocations && invoiceAny.PatientPaymentAllocations.length > 0) {
        invoiceAny.PatientPaymentAllocations.forEach((allocation: any) => {
          if (allocation.PatientPayment) {
            const payment = allocation.PatientPayment;
            const amount = allocation.allocatedAmount || 0;
            const category = this.categorizePaymentMethod(payment.paymentMethod || '');

            if (category === 'cash') {
              cashAmount += amount;
            } else if (category === 'credited') {
              creditedAmount += amount;
            } else if (category === 'electronic') {
              electronicAmount += amount;
            }
          }
        });
      }
    });

    // Process receipts (which are linked to payments)
    receipts.forEach(receipt => {
      const receiptAny = receipt as any;
      if (receiptAny.PatientPayment) {
        const payment = receiptAny.PatientPayment;
        const amount = receipt.totalDue || 0;
        const category = this.categorizePaymentMethod(payment.paymentMethod || '');

        if (category === 'cash') {
          cashAmount += amount;
        } else if (category === 'credited') {
          creditedAmount += amount;
        } else if (category === 'electronic') {
          electronicAmount += amount;
        }
      } else {
        // Fallback: try to extract payment method from notes
        const notes = receiptAny.notes?.toLowerCase() || '';
        const amount = receipt.totalDue || 0;

        if (notes.includes('cash')) {
          cashAmount += amount;
        } else if (notes.includes('credit:')) {
          creditedAmount += amount;
        } else if (notes.includes('yoco') || notes.includes('card') || notes.includes('eft')) {
          electronicAmount += amount;
        }
      }
    });

    return {
      totalInvoiced,
      totalPaid,
      totalOutstanding,
      cashAmount,
      electronicAmount,
      creditedAmount
    };
  }

  getAppointmentFinancialStatus(appointment: any): string {
    if (!appointment.Patient) return 'none';

    // First try to get documents directly linked to this appointment
    let patientDocuments: BillingDocument[] = [];
    
    if (appointment.id) {
      patientDocuments = this.getBillingDocumentsForAppointment(appointment.id);
    }
    
    // Fallback: find documents for this patient on this date
    if (patientDocuments.length === 0) {
      const dateKey = moment(appointment.date).format('YYYY-MM-DD');
      const patientId = appointment.Patient.id;

      patientDocuments = (this.billingDocsByDate.get(dateKey) || []).filter(doc => doc.patientId === patientId);
    }

    if (patientDocuments.length === 0) return 'none';

    // Check for invoices first
    const invoices = patientDocuments.filter(doc => doc.documentType === 'invoice');
    if (invoices.length > 0) {
      // Use the first invoice for status (assuming one invoice per appointment)
      const invoice = invoices[0];

      if (invoice.outstandingBalance === 0) {
        return 'paid'; // Green
      } else if (invoice.outstandingBalance < invoice.totalDue) {
        return 'partially_paid'; // Orange
      } else {
        return 'unpaid'; // Red
      }
    }

    // Check for estimates if no invoices
    const estimates = patientDocuments.filter(doc => doc.documentType === 'estimate');
    if (estimates.length > 0) {
      return 'estimate'; // Grey
    }

    return 'none';
  }

  getAppointmentStatusColor(status: string): string {
    switch (status) {
      case 'paid':
        return '#4caf50'; // Green
      case 'partially_paid':
        return '#ff9800'; // Orange
      case 'unpaid':
        return '#f44336'; // Red
      case 'estimate':
        return '#9e9e9e'; // Grey
      default:
        return 'transparent';
    }
  }

  getAppointmentStatusTooltip(status: string): string {
    switch (status) {
      case 'paid':
        return 'Invoice fully paid';
      case 'partially_paid':
        return 'Invoice partially paid';
      case 'unpaid':
        return 'Invoice unpaid';
      case 'estimate':
        return 'Estimate created, no invoice yet';
      default:
        return 'No financial documents';
    }
  }

  getAppointmentStatusLabel(status: string): string {
    switch (status) {
      case 'paid':
        return 'Paid';
      case 'partially_paid':
        return 'Partially Paid';
      case 'unpaid':
        return 'Unpaid';
      case 'estimate':
        return 'Estimate';
      default:
        return 'No Invoice';
    }
  }

  getAppointmentCancellationTooltip(appointment: any): string {
    if (appointment.isCancelled === true) {
      return 'Appointment cancelled';
    }
    if (appointment.didNotAttend === true) {
      return 'Patient did not attend - No invoice expected';
    }
    return '';
  }

  getTotalAppointmentsCount(): number {
    if (!this.appointmentsForSelectedDay) return 0;
    // Count only actual appointments (not events/reasons)
    return this.appointmentsForSelectedDay.filter(appointment => !appointment.reason).length;
  }

  getInvoicedAppointmentsCount(): number {
    if (!this.appointmentsForSelectedDay) return 0;
    
    let invoicedCount = 0;
    
    this.appointmentsForSelectedDay.forEach(appointment => {
      // Skip events/reasons
      if (appointment.reason) return;
      
      // Skip cancelled or no-show appointments
      if (appointment.isCancelled === true || appointment.didNotAttend === true) return;
      
      // Check if this appointment has any billing documents
      const appointmentBillingDocs = this.getBillingDocumentsForAppointment(appointment.id);
      if (appointmentBillingDocs.length > 0) {
        // Check if any of the documents are invoices (not just estimates)
        const hasInvoice = appointmentBillingDocs.some(doc => doc.documentType === 'invoice');
        if (hasInvoice) {
          invoicedCount++;
        }
      }
    });
    
    return invoicedCount;
  }

  getAppointmentsForDate(date: Date): any[] {
    if (!date) return FinancialCalendarComponent.EMPTY_APPOINTMENTS;
    return this.appointmentsByDate.get(this.getDateKey(date)) || FinancialCalendarComponent.EMPTY_APPOINTMENTS;
  }

  getTotalAppointmentsForDate(date: Date): number {
    if (!date) return 0;
    return this.totalAppointmentsByDate.get(this.getDateKey(date)) || 0;
  }

  getInvoicedAppointmentsForDate(date: Date): number {
    if (!date) return 0;
    return this.invoicedAppointmentsByDate.get(this.getDateKey(date)) || 0;
  }

  getLinkedDocumentsByBillingNumber(billingNumber: string): BillingDocument[] {
    // Extract the core number from the billing number (e.g., "251007115532730" from "INV-251007115532730")
    const coreNumber = billingNumber.replace(/^(INV-|RCPT-|EST-)/, '');
    return this.docsByCoreBillingNumber.get(coreNumber) || FinancialCalendarComponent.EMPTY_DOCUMENTS;
  }

  isProBonoPayment(doc: BillingDocument): boolean {
    // For receipts, check if this specific receipt has Pro Bono payment method
    if (doc.documentType === 'receipt' && doc.PatientPayment) {
      const paymentMethod = (doc.PatientPayment.paymentMethod || '').toLowerCase();
      return paymentMethod.includes('pro bono') || paymentMethod.includes('pro-bono') || paymentMethod.includes('probono');
    }
    
    // For invoices, check if there are any linked Pro Bono receipts with the same billing number
    if (doc.documentType === 'invoice') {
      const linkedDocs = this.getLinkedDocumentsByBillingNumber(doc.billingNumber);
      
      return linkedDocs.some(linkedDoc => {
        if (linkedDoc.documentType === 'receipt' && linkedDoc.PatientPayment) {
          const paymentMethod = (linkedDoc.PatientPayment.paymentMethod || '').toLowerCase();
          return paymentMethod.includes('pro bono') || paymentMethod.includes('pro-bono') || paymentMethod.includes('probono');
        }
        return false;
      });
    }
    
    return false;
  }

  getDocumentStatusLabel(doc: BillingDocument): string {
    // Check for Pro Bono payments first
    const isProBono = this.isProBonoPayment(doc);
    
    if (isProBono) {
      return 'Pro Bono';
    }
    
    // For receipts, show "Payment Received" instead of status
    if (doc.documentType === 'receipt') {
      return 'Payment Received';
    }
    
    // For estimates, show "Estimate" instead of status
    if (doc.documentType === 'estimate') {
      return 'Estimate';
    }
    
    // For invoices, use the status label
    const statusLabel = (() => {
      switch (doc.status) {
        case 'paid':
          return 'Paid';
        case 'partially_paid':
          return 'Partially Paid';
        case 'pending':
          return 'Pending';
        case 'overdue':
          return 'Overdue';
        case 'draft':
          return 'Draft';
        case 'cancelled':
          return 'Cancelled';
        default:
          return 'No Invoice';
      }
    })();
    
    return statusLabel;
  }

  getDocumentStatusChipClass(status: string): string {
    switch (status) {
      case 'paid':
        return 'green-chip';
      case 'partially_paid':
        return 'orange-chip';
      case 'pending':
        return 'orange-chip';
      case 'overdue':
        return 'red-chip';
      case 'draft':
        return 'orange-chip';
      case 'cancelled':
      case 'unpaid':
        return 'red-chip';
      default:
        return 'grey-chip';
    }
  }

  getDocumentStatusChipClassForDocument(doc: BillingDocument): string {
    // Check for Pro Bono payments first - show blue chip
    if (this.isProBonoPayment(doc)) {
      return 'blue-chip';
    }
    
    // For receipts, show green chip for "Payment Received"
    if (doc.documentType === 'receipt') {
      return 'green-chip';
    }
    
    // For estimates, show orange chip for "Estimate"
    if (doc.documentType === 'estimate') {
      return 'grey-chip';
    }
    
    // For invoices, use the status-based chip class
    return this.getDocumentStatusChipClass(doc.status);
  }

  toggleFinancialDocuments(): void {
    this.financialDocumentsExpanded = !this.financialDocumentsExpanded;
  }

  toggleAppointments(): void {
    this.appointmentsExpanded = !this.appointmentsExpanded;
  }

  toggleFinancialDocs(appointment: any) {
    const id = appointment.id;
    this.financialDocsExpanded[id] = !this.financialDocsExpanded[id];
  }
  isFinancialDocsExpanded(appointment: any): boolean {
    // Return true by default if not explicitly set, otherwise return the stored value
    return this.financialDocsExpanded[appointment.id] !== false;
  }

  /**
   * Returns the financial summary for a specific date (not just selected day)
   */
  getFinancialSummaryForDate(date: Date, includeUnlinked?: boolean): NativeDayFinancialSummary {
    if (!date) return FinancialCalendarComponent.EMPTY_DAY_SUMMARY;
    const summaryMap = includeUnlinked ? this.financialSummaryWithUnlinkedByDate : this.financialSummaryByDate;
    return summaryMap.get(this.getDateKey(date)) || FinancialCalendarComponent.EMPTY_DAY_SUMMARY;
  }

  getEstimateTotalForDate(date: Date): number {
    if (!date) return 0;
    return this.estimateTotalByDate.get(this.getDateKey(date)) || 0;
  }

  // State for expanded/collapsed unlinked patient groups
  unlinkedPatientGroupExpanded: { [patientId: string]: boolean } = {};

  /**
   * Returns unlinked financial documents for the selected day, grouped by patient
   */
  getUnlinkedFinancialDocumentsGroupedByPatient() {
    const cacheKey = this.selectedDay && this.unlinkedBillingDocuments
      ? `${moment(this.selectedDay).format('YYYY-MM-DD')}_${this.unlinkedBillingDocuments.length}`
      : '';
    if (this.unlinkedFinancialDocumentsGroupedCacheKey === cacheKey) {
      return this.unlinkedFinancialDocumentsGroupedCache;
    }
    if (!this.selectedDay || !this.unlinkedBillingDocuments) return [];
    const targetDate = moment(this.selectedDay).startOf('day');
    const unlinkedDocs = this.unlinkedBillingDocuments.filter(doc => {
      const docDate = moment(doc.issueDate).startOf('day');
      return docDate.isSame(targetDate, 'day');
    });
    const grouped = _.groupBy(unlinkedDocs, doc => doc.Patient ? doc.Patient.id : 'unknown');
    const result = Object.entries(grouped)
      .map(([patientId, documents]) => ({
        patientId,
        patientName: documents[0].Patient ? `${documents[0].Patient.surname}, ${documents[0].Patient.name}` : 'Unknown Patient',
        documents: documents.slice().sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      }))
      .sort((a, b) => a.patientName.localeCompare(b.patientName));
    this.unlinkedFinancialDocumentsGroupedCache = result;
    this.unlinkedFinancialDocumentsGroupedCacheKey = cacheKey;
    return result;
  }

  toggleUnlinkedPatientGroup(patientId: string) {
    this.unlinkedPatientGroupExpanded[patientId] = !this.unlinkedPatientGroupExpanded[patientId];
  }

  isUnlinkedPatientGroupExpanded(patientId: string): boolean {
    // Return true by default if not explicitly set, otherwise return the stored value
    return this.unlinkedPatientGroupExpanded[patientId] !== false;
  }

  // Helper for sorting linked docs in template
  getSortedBillingDocumentsForAppointment(appointmentId: string) {
    return this.sortedBillingDocsByAppointmentId.get(appointmentId) || FinancialCalendarComponent.EMPTY_DOCUMENTS;
  }
}